import { useMarketplacePreviewItems } from '@/apis/marketplace-preview/hooks/useMarketplacePreviewItems'
import { MarketplaceItem } from '@/apis/marketplace/marketplace.schema'
import MultiQueryPageLoader from '@/app/common/components/PageLoader/MultiQueryPageLoader'
import { usePagination } from '@/common/hooks/usePagination.tsx'
import { AppRoutePaths } from '@/routing/route_paths'
import { useDebounce } from '@uidotdev/usehooks'
import { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { TABLE_FILTERS } from '../../constants'
import AffiliationItems from '../../marketplace-live/affiliations/AffiliationItems'
import { MarketplaceAccessPageProps } from '@/apis/marketplace-preview/types'

export default function MarketplacePreviewAffiliationDetailsPage({
  mode = 'preview',
}: MarketplaceAccessPageProps) {
  const navigate = useNavigate()
  const marketplacePaths =
    mode === 'published'
      ? AppRoutePaths.marketplace_view
      : AppRoutePaths.marketplace_preview
  const { affiliationId } = useParams()
  const { currentPage, perPage, setCurrentPage, setPerPage } = usePagination()

  const [search, setSearch] = useState('')
  const [selectedFilter, setSelectedFilter] = useState<string>('')

  const debouncedSearch = useDebounce(search, 500)
  const parsedAffiliationId = Number(affiliationId)

  const isValidAffiliationId =
    Number.isSafeInteger(parsedAffiliationId) && parsedAffiliationId > 0

  const itemsQuery = useMarketplacePreviewItems(
    {
      perPage,
      page: currentPage,
      search: debouncedSearch,
      business_type: selectedFilter,
      affiliation_id: isValidAffiliationId ? parsedAffiliationId : undefined,
    },
    mode,
    {
      enabled: isValidAffiliationId,
    }
  )

  const items = itemsQuery.data?.data ?? []
  const totalRecords = itemsQuery.data?.total ?? 0

  const handleFilterOnChange = (item: { id: number; name: string }) => {
    setSelectedFilter(
      item.id == 0 ? '' : item.id == 1 ? '1,2' : item.id.toString()
    )
    setCurrentPage(1)
  }

  const handleItemClick = (item: MarketplaceItem) => {
    navigate(marketplacePaths.details.replace(':itemId', item.id.toString()), {
      state: { item },
    })
  }

  const handleBack = () => {
    navigate(marketplacePaths.index)
  }

  if (!isValidAffiliationId) {
    return (
      <div className="text-center text-gray-500 py-10">
        Affiliation not found.
      </div>
    )
  }

  return (
    <MultiQueryPageLoader queries={[itemsQuery] as const}>
      {() => (
        <AffiliationItems
          items={items}
          totalRecords={totalRecords}
          search={search}
          filters={TABLE_FILTERS}
          currentPage={currentPage}
          perPage={perPage}
          onSearchChange={setSearch}
          onFilterChange={handleFilterOnChange}
          onItemClick={handleItemClick}
          onBack={handleBack}
          onPageChange={setCurrentPage}
          onPerPageChange={setPerPage}
        />
      )}
    </MultiQueryPageLoader>
  )
}
